/*
  Animated (typing-effect) placeholders for the header quick search.

  Reads the phrase list from the `data-animated-placeholders` attribute that
  snippets/quick-search.liquid renders on `[data-quick-search]`, then cycles
  through the phrases: hold -> backspace only the part that differs from the
  next phrase -> type the next phrase -> hold -> ...

  It stays out of the way of the theme's own QuickSearch component: the
  animation pauses whenever the search is open, the user has typed something,
  or a product type is selected, and it resumes once the bar is idle again.
*/
(function () {
  var SELECTOR = "[data-quick-search][data-animated-placeholders]";
  var TYPE_MS = 65;
  var DELETE_MS = 35;
  var DEFAULT_HOLD_MS = 1800;
  var PRE_TYPE_MS = 250;
  var IDLE_POLL_MS = 400;

  var reduceMotion =
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Length of the shared leading text between two phrases, trimmed back to a
  // word boundary so we never leave half a word behind.
  function sharedPrefixLength(a, b) {
    var max = Math.min(a.length, b.length);
    var i = 0;
    while (i < max && a.charAt(i) === b.charAt(i)) i++;
    if (i === a.length && i === b.length) return i; // identical
    var boundary = a.lastIndexOf(" ", i - 1);
    return boundary === -1 ? 0 : boundary + 1;
  }

  function init(node) {
    if (node.dataset.animatedPlaceholdersInit === "true") return;
    node.dataset.animatedPlaceholdersInit = "true";

    var items;
    try {
      items = JSON.parse(node.getAttribute("data-animated-placeholders"));
    } catch (e) {
      return;
    }
    if (!Array.isArray(items)) return;

    var scope = node.dataset.quickSearchScope;
    var holdMs =
      parseInt(node.getAttribute("data-animated-placeholders-pause"), 10) ||
      DEFAULT_HOLD_MS;

    var phrases = items
      .filter(function (item) {
        if (!item || typeof item.text !== "string") return false;
        var text = item.text.trim();
        if (!text) return false;
        if (scope === "mobile" && !item.mobile) return false;
        return true;
      })
      .map(function (item) {
        return item.text.trim();
      });

    if (!phrases.length) return;

    var fauxInput = node.querySelector("[data-quick-search-faux-input]");
    var fauxText = node.querySelector("[data-quick-search-faux-text]");
    var input = node.querySelector("[data-quick-search-input]");
    var fauxTypeSelect = node.querySelector(
      "[data-quick-search-faux-product-type]"
    );

    if (!fauxInput || !fauxText) return;

    // The open search field shows no placeholder (hidden via CSS in
    // sections/header.liquid), so nothing is written to the real input.
    function setFullPlaceholder() {}

    function render(text) {
      fauxText.textContent = text;
    }

    // Single phrase or reduced motion: show it statically, no animation.
    if (phrases.length === 1 || reduceMotion) {
      render(phrases[0]);
      setFullPlaceholder(phrases[0]);
      return;
    }

    var index = 0;
    var deleteTo = 0;
    var timer = null;

    function schedule(fn, ms) {
      clearTimeout(timer);
      timer = setTimeout(fn, ms);
    }

    function isIdle() {
      if (node.dataset.quickSearchActive === "true") return false;
      if (fauxInput.dataset.placeholderActive === "false") return false;
      if (input && input.value !== "") return false;
      if (fauxTypeSelect && fauxTypeSelect.value) return false;
      return true;
    }

    function stop() {
      clearTimeout(timer);
      node.removeAttribute("data-animating");
    }

    // Wait until the bar is idle again, then restart from the current phrase.
    function pause() {
      node.dataset.animating = "false";
      schedule(function () {
        if (!node.isConnected) return stop();
        if (isIdle()) {
          hold();
        } else {
          pause();
        }
      }, IDLE_POLL_MS);
    }

    function hold() {
      if (!node.isConnected) return stop();
      if (!isIdle()) return pause();
      node.dataset.animating = "true";
      render(phrases[index]);
      setFullPlaceholder(phrases[index]);
      schedule(startDelete, holdMs);
    }

    function startDelete() {
      if (!node.isConnected) return stop();
      if (!isIdle()) return pause();
      var next = phrases[(index + 1) % phrases.length];
      deleteTo = sharedPrefixLength(phrases[index], next);
      deleteStep();
    }

    function deleteStep() {
      if (!node.isConnected) return stop();
      if (!isIdle()) return pause();
      var current = fauxText.textContent;
      if (current.length > deleteTo) {
        render(current.slice(0, -1));
        schedule(deleteStep, DELETE_MS);
      } else {
        index = (index + 1) % phrases.length;
        setFullPlaceholder(phrases[index]);
        schedule(typeStep, PRE_TYPE_MS);
      }
    }

    function typeStep() {
      if (!node.isConnected) return stop();
      if (!isIdle()) return pause();
      var target = phrases[index];
      var current = fauxText.textContent;
      if (target.indexOf(current) !== 0) current = "";
      if (current.length < target.length) {
        render(target.slice(0, current.length + 1));
        schedule(typeStep, TYPE_MS);
      } else {
        schedule(startDelete, holdMs);
      }
    }

    // Kick off after a short delay so the page settles first.
    render(phrases[0]);
    setFullPlaceholder(phrases[0]);
    schedule(hold, 600);
  }

  function initAll(root) {
    var nodes = (root || document).querySelectorAll(SELECTOR);
    for (var i = 0; i < nodes.length; i++) init(nodes[i]);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Theme editor re-renders the header section on setting changes.
  document.addEventListener("shopify:section:load", function (event) {
    initAll(event.target);
  });
})();
